import { useFormikContext } from 'formik';
import { useCallback, useState } from 'react';

import type { ProfitItemData } from '@frontend/api';
import {
  TableContainer,
  TableFooter,
  TableV2,
  Tbody,
  Th,
  Td,
  Thead,
  Tr,
  Button,
} from '@frontend/components';

import { NewProfitItemRow } from './NewProfitItemRow';
import { ProfitItemRow, ProfitItemRowProps } from './ProfitItemRow';
import type { FormValues } from './types';

export const ProfitItemsTable = () => {
  const { setFieldValue, values } = useFormikContext<FormValues>();

  const [isAdding, setIsAdding] = useState(false);

  const addProfitItem = useCallback(
    (profitItem: ProfitItemData) => {
      const newProfitItems = [...values.profitItems, profitItem];
      setIsAdding(false);
      setFieldValue('profitItems', newProfitItems);
    },
    [setFieldValue, values.profitItems],
  );

  const removeProfitItem = useCallback<ProfitItemRowProps['onRemove']>(
    (id) => {
      const newProfitItems = values.profitItems.filter(
        (profitItem) => profitItem.id !== id,
      );
      setFieldValue('profitItems', newProfitItems);
    },
    [setFieldValue, values.profitItems],
  );

  const editProfitItem = useCallback<ProfitItemRowProps['onEdit']>(
    (id, data) => {
      const newProfitItems = values.profitItems.map((profitItem) =>
        profitItem.id === id
          ? ({ ...profitItem, ...data } as ProfitItemData)
          : profitItem,
      );
      setFieldValue('profitItems', newProfitItems);
    },
    [setFieldValue, values.profitItems],
  );

  return (
    <TableContainer flexShrink={0}>
      <TableV2>
        <Thead>
          <Tr>
            <Th>Pricing</Th>
            <Th w={24} pr={2} pl={0.25} textAlign="right">
              Percent (All Qtys)
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {!values.profitItems.length && !isAdding && (
            <Tr>
              <Td colSpan={2} textAlign="center" fontWeight="heavy">
                No pricing items
              </Td>
            </Tr>
          )}
          {values.profitItems.map((profitItem) => (
            <ProfitItemRow
              key={profitItem.id}
              profitItem={profitItem}
              onEdit={editProfitItem}
              onRemove={removeProfitItem}
            />
          ))}
          {isAdding && (
            <NewProfitItemRow
              onClose={() => setIsAdding(false)}
              onAdd={addProfitItem}
            />
          )}
        </Tbody>
      </TableV2>
      <TableFooter bg="page.plateLight">
        <Button
          size="sm"
          isDisabled={!values.action || isAdding}
          onClick={() => setIsAdding(true)}
        >
          Add Pricing Item
        </Button>
      </TableFooter>
    </TableContainer>
  );
};
